import { useCallback, useMemo, useRef } from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import {
  getElementLogicalInlineStartOffset,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import type { AccommodationDayView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import type { AccommodationDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import { OfferPageActions, type OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { getLocalDate } from 'lib/datetime/calendarUtils'
import { diffInDays, formatIsoDate } from 'lib/datetime/dateUtils'
import type { MonthDayView } from 'selectors/offerPage/offerPageSelectors'
import { getSoldOutRoomCalendarDays } from 'selectors/offerPage/offerPageSelectors'
import FlashHotelSoldOutDatesCarouselDayCell from './FlashHotelSoldOutDatesCarouselDayCell'
import { mediaQueryUp } from 'components/utils/breakpoint'

const DAYS_IN_WEEK = 7
const DAY_CELL_GAP = 10
const TARGET_VISIBLE_DAY_INDEX = 3
const DESKTOP_DAY_CELL_WIDTH = `calc((100% - ${rem(DAY_CELL_GAP * (DAYS_IN_WEEK - 1))}) / ${DAYS_IN_WEEK})`

const CarouselContainer = styled.div`
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)};
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

interface Props {
  roomType: App.PackageRoomType
  daysByMonth: Array<MonthDayView>
  displayMemberPrice: boolean
  selectedCheckInDate?: string
}

function getDisplayPricing(day: App.CalendarDay, displayMemberPrice: boolean): App.Pricing | undefined {
  return displayMemberPrice ? (day.memberPricing ?? day.pricing) : day.pricing
}

function mapCalendarDayToAccommodationDayView(day: App.CalendarDay, displayMemberPrice: boolean): AccommodationDayView<App.CalendarDay> {
  const checkInDate = getLocalDate(day.checkIn)
  const checkOutDate = getLocalDate(day.checkOut)
  const pricing = day.hotelAvailable ? getDisplayPricing(day, displayMemberPrice) : undefined

  return {
    date: day.checkIn,
    year: checkInDate.getFullYear(),
    month: checkInDate.getMonth(),
    day: checkInDate.getDate(),
    duration: Math.max(moment(checkOutDate).diff(moment(checkInDate), 'days'), 0),
    durationUnit: 'night',
    pricing,
    priceType: displayMemberPrice && !!day.memberPricing ? 'member' : 'default',
    soldOut: !day.hotelAvailable,
    featured: day.hasTactical ? 'special' : undefined,
    additionalData: day,
  }
}

function getVisibleCalendarDays(days: Array<App.CalendarDay>, startDate: string): Array<App.CalendarDay> {
  return days.filter(day => day.checkIn >= startDate)
}

function getDayDistance(date: Date, referenceDate: Date): number {
  const distance = diffInDays(date, referenceDate)
  return distance < 0 ? -distance : distance
}

function getInitialCarouselIndex(
  days: Array<AccommodationDayView<App.CalendarDay>>,
  targetVisibleDayIndex: number,
  selectedCheckInDate?: string,
): number {
  if (!selectedCheckInDate) {
    return 0
  }

  const selectedDate = getLocalDate(selectedCheckInDate)
  const closestAvailableIndex = days.reduce<number | undefined>((closestIndex, day, index) => {
    if (day.soldOut) {
      return closestIndex
    }

    if (closestIndex === undefined) {
      return index
    }

    const currentDistance = getDayDistance(getLocalDate(day.date), selectedDate)
    const closestDistance = getDayDistance(getLocalDate(days[closestIndex].date), selectedDate)
    return currentDistance < closestDistance ? index : closestIndex
  }, undefined)

  if (closestAvailableIndex === undefined) {
    return 0
  }

  return Math.max(0, closestAvailableIndex - targetVisibleDayIndex)
}

function FlashHotelSoldOutDatesCarousel(props: Props) {
  const { daysByMonth, displayMemberPrice, selectedCheckInDate } = props
  const offerPageDispatch = useOfferPageDispatch()
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useScreenSizeOnly('mobile')

  const today = useMemo(() => formatIsoDate(new Date()), [])

  const days = useMemo(() => {
    const allDays = daysByMonth.flatMap(([, days]) => days)
    return getVisibleCalendarDays(allDays, today)
      .map(day => mapCalendarDayToAccommodationDayView(day, displayMemberPrice))
  }, [daysByMonth, displayMemberPrice, today])

  const targetVisibleDayIndex = isMobile ? 0 : TARGET_VISIBLE_DAY_INDEX
  const initialCarouselIndex = useMemo(() => getInitialCarouselIndex(days, targetVisibleDayIndex, selectedCheckInDate), [days, targetVisibleDayIndex, selectedCheckInDate])

  const onDaySelect = useCallback<AccommodationDayCellSelectHandler>((day) => {
    const calendarDay = day.additionalData
    if (calendarDay?.hotelAvailable) {
      offerPageDispatch({
        type: OfferPageActions.setDates,
        checkIn: calendarDay.checkIn,
        checkOut: calendarDay.checkOut,
      })
    }
  }, [offerPageDispatch])

  const dayCells = useMemo(() => days.map(day => <FlashHotelSoldOutDatesCarouselDayCell
    key={day.date}
    day={day}
    onClick={onDaySelect}
  />), [days, onDaySelect])

  useIsomorphicLayoutEffect(() => {
    const carousel = carouselRef.current
    const initialCell = carousel?.children[initialCarouselIndex] as HTMLElement | undefined
    if (!carousel || !initialCell) {
      return
    }

    scrollToLogicalPosition(carousel, {
      left: getElementLogicalInlineStartOffset(initialCell, carousel),
      behavior: 'instant',
    })
  }, [initialCarouselIndex])

  if (days.length < DAYS_IN_WEEK) {
    return null
  }

  return <CarouselContainer>
    <CardCarousel
      snap="start"
      pageSize="auto"
      width={rem(125)}
      desktopWidth={DESKTOP_DAY_CELL_WIDTH}
      gap={DAY_CELL_GAP}
      ref={carouselRef}
    >
      {dayCells}
    </CardCarousel>
  </CarouselContainer>
}

type MappedProps = Pick<Props, 'daysByMonth' | 'displayMemberPrice' | 'selectedCheckInDate'>

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State, props: Props): MappedProps => ({
  daysByMonth: getSoldOutRoomCalendarDays(appState, state, props.roomType),
  displayMemberPrice: !!state.displayMemberPrice,
  selectedCheckInDate: state.checkInDate,
}), FlashHotelSoldOutDatesCarousel)
